import { useRef, useState, useCallback, useEffect } from "react";
import { Reveal } from "@/components/Reveal";
import { MoveHorizontal } from "lucide-react";
import { ASSETS } from "@/constants/assets";

export const BeforeAfter = () => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const [width, setWidth] = useState(0);
  const dragging = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [setFromClientX]);

  return (
    <section id="results" data-testid="results-section" className="py-20 sm:py-28 bg-cream">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-turquoise">
            Real results
          </p>
          <h2 className="mt-3 font-display fluid-h2 font-semibold text-navy">
            See the transformation{" "}
            <span className="italic text-turquoise">for yourself</span>
          </h2>
          <p className="mt-4 text-navy/65 text-lg">
            Drag the handle to reveal the difference on a real entrance carpet we cleaned in Lanarkshire.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <div
            ref={containerRef}
            data-testid="before-after-slider"
            className="relative aspect-[4/3] sm:aspect-[16/10] w-full overflow-hidden rounded-3xl shadow-2xl shadow-navy/25 ring-1 ring-navy/10 no-select cursor-ew-resize"
            onMouseDown={(e) => {
              dragging.current = true;
              setFromClientX(e.clientX);
            }}
            onTouchStart={(e) => {
              dragging.current = true;
              setFromClientX(e.touches[0].clientX);
            }}
          >
            {/* AFTER (base, clean) */}
            <img
              src={ASSETS.hallwayAfter}
              alt="Entrance hallway carpet after professional cleaning by Lanarkshire Cleaning Company"
              draggable={false}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <span className="absolute right-4 top-4 z-20 rounded-full bg-turquoise px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-lg">
              After
            </span>

            {/* BEFORE (clipped overlay, dirty) */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${pos}%` }}
            >
              <img
                src={ASSETS.hallwayBefore}
                alt="Entrance hallway carpet before cleaning, showing stains and heavy soiling"
                draggable={false}
                style={{ width: width || "100%" }}
                className="absolute inset-0 h-full max-w-none object-cover"
              />
              <span className="absolute left-4 top-4 z-20 rounded-full bg-navy px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-lg">
                Before
              </span>
            </div>

            {/* Handle */}
            <div
              className="absolute inset-y-0 z-30 flex items-center"
              style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
            >
              <div className="h-full w-0.5 bg-white shadow-[0_0_12px_rgba(0,0,0,0.35)]" />
              <div className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-navy shadow-xl ring-2 ring-turquoise transition-transform duration-200 hover:scale-110">
                <MoveHorizontal className="h-5 w-5" />
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
